import React, { useState } from "react";

import { DeleteOutlined, ExportOutlined } from "@ant-design/icons";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { Button, Divider, FormLabel } from "@impero-com/ui";
import { Popover } from "antd";

import { Drawer } from "components/drawer";
import { DrawerHeaderLabel } from "components/drawerHeaderLabel/drawerHeaderLabel";
import { buildLegacyControlActivityUrl } from "org/controlTesting/urlUtils";

import { PopoverContent } from "../../PopoverContent/PopoverContent";

interface SampleDrawerContentProps {
    open: boolean;
    onClose: () => void;
    onRemove: () => void;
    onViewActivityClick: () => void;
    activityId: string;
    activityTitle: string;
    children: React.ReactNode;
    hasViewRight: boolean;
    canRemove: boolean;
}
export function SampleDrawerContent({
    open,
    onClose,
    onRemove,
    onViewActivityClick,
    activityId,
    activityTitle,
    children,
    hasViewRight,
    canRemove,
}: SampleDrawerContentProps) {
    const { t } = useTranslation();

    const openLabel = t("ControlTestingActivityView_SampleDrawer_Open");

    // A disabled link must not navigate, so it drops the anchor entirely.
    const viewActivity = hasViewRight ? (
        <Button variant="link" icon={<ExportOutlined />} asChild>
            <a
                href={buildLegacyControlActivityUrl(activityId)}
                onClick={onViewActivityClick}
                target="_blank"
                rel="noopener noreferrer"
            >
                {openLabel}
            </a>
        </Button>
    ) : (
        <Popover content={t("InsufficientAccess")}>
            <Button variant="link" icon={<ExportOutlined />} disabled>
                {openLabel}
            </Button>
        </Popover>
    );

    return (
        <Drawer
            open={open}
            onCancel={onClose}
            buildHeader={() => (
                <DrawerHeaderLabel
                    label={t("ControlTestingActivityView_SampleDrawer_Title")}
                />
            )}
            buildFooter={() => (
                <Footer
                    onClose={onClose}
                    onRemove={onRemove}
                    canRemove={canRemove}
                />
            )}
        >
            <FormLabel>
                {t("ControlTestingActivityView_SampleDrawer_ControlToTest")}
            </FormLabel>
            <div>
                {activityTitle}
                {viewActivity}
            </div>
            <Divider />
            {children}
        </Drawer>
    );
}

interface FooterProps {
    onClose: () => void;
    onRemove: () => void;
    canRemove: boolean;
}
function Footer({ onClose, onRemove, canRemove }: FooterProps) {
    const { t } = useTranslation();
    const [popoverOpen, setPopoverOpen] = useState(false);
    const handleRemove = () => {
        setPopoverOpen(false);
        onRemove();
    };
    return (
        <div>
            <Button variant="primary" onClick={onClose}>
                {t("Confirm")}
            </Button>
            {canRemove && (
                <Popover
                    content={
                        <PopoverContent
                            onRemove={handleRemove}
                            onCancel={() => setPopoverOpen(false)}
                            warnLabel={t(
                                "ControlTestingActivityView_SampleDrawer_Warn"
                            )}
                        />
                    }
                    open={popoverOpen}
                    onOpenChange={setPopoverOpen}
                    trigger="click"
                >
                    <Button variant="link" icon={<DeleteOutlined />}>
                        {t(
                            "ControlTestingActivityView_SampleDrawer_RemoveButton"
                        )}
                    </Button>
                </Popover>
            )}
        </div>
    );
}
